import type { ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type { ProductListItem } from '@/types/product';
import {
  ROW_STATUS,
  groupCheckState,
  groupPriceRange,
  groupStatusSummary,
  lastChangeLabel,
  metaLabel,
  priceLabel,
  rowStatus,
  usageLabel,
  type ProductGroup,
} from './productListView';

/**
 * Kolombreedtes staan op één plek, want de groepsrijen moeten exact dezelfde
 * banen aanhouden als de productrijen. Zodra die uit de pas lopen, leest de
 * tabel als een verzameling losse regels.
 */
const COL = {
  status: 'w-[140px] shrink-0',
  price: 'w-[140px] shrink-0 text-right',
  usage: 'w-[92px] shrink-0 text-right',
  changed: 'w-[132px] shrink-0',
  reviewers: 'hidden w-[132px] shrink-0 xl:flex',
  active: 'w-[72px] shrink-0',
};

interface ProductTableProps {
  groups: ProductGroup[];
  grouped: boolean;
  collapsed: Set<string>;
  selected: Set<string>;
  onToggleCollapse: (key: string) => void;
  onToggleGroup: (group: ProductGroup, select: boolean) => void;
  onToggleProduct: (id: string, shiftKey: boolean) => void;
  onOpen: (id: string) => void;
  onSetEnabled: (product: ProductListItem, enabled: boolean) => void;
  busyIds: Set<string>;
}

export function ProductTable({
  groups,
  grouped,
  collapsed,
  selected,
  onToggleCollapse,
  onToggleGroup,
  onToggleProduct,
  onOpen,
  onSetEnabled,
  busyIds,
}: ProductTableProps) {
  return (
    <div className="overflow-hidden rounded-md border bg-card">
      <div className="flex h-9 items-center gap-4 border-b bg-muted px-4">
        <div className="w-4 shrink-0" />
        <HeaderCell className="min-w-0 flex-1">Product</HeaderCell>
        <HeaderCell className={COL.status}>Status</HeaderCell>
        <HeaderCell className={COL.price}>Prijs</HeaderCell>
        <HeaderCell className={COL.usage}>In gebruik</HeaderCell>
        <HeaderCell className={COL.changed}>Bijgewerkt</HeaderCell>
        <HeaderCell className={COL.reviewers}>Reviewers</HeaderCell>
        <HeaderCell className={cn(COL.active, 'text-right')}>Actief</HeaderCell>
      </div>

      {groups.map((group) => {
        const isCollapsed = grouped && collapsed.has(group.key);
        const check = groupCheckState(group, selected);
        return (
          <div key={group.key}>
            {grouped && (
              <GroupRow
                group={group}
                collapsed={isCollapsed}
                check={check}
                selectedCount={group.products.reduce(
                  (total, product) => total + (selected.has(product.id) ? 1 : 0),
                  0,
                )}
                onToggleCollapse={() => onToggleCollapse(group.key)}
                onToggleSelection={() => onToggleGroup(group, check !== 'all')}
              />
            )}
            {!isCollapsed &&
              group.products.map((product) => (
                <ProductRow
                  key={product.id}
                  product={product}
                  selected={selected.has(product.id)}
                  busy={busyIds.has(product.id)}
                  onToggle={(shiftKey) => onToggleProduct(product.id, shiftKey)}
                  onOpen={() => onOpen(product.id)}
                  onSetEnabled={(enabled) => onSetEnabled(product, enabled)}
                />
              ))}
          </div>
        );
      })}
    </div>
  );
}

function HeaderCell({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground',
        className,
      )}
    >
      {children}
    </div>
  );
}

interface GroupRowProps {
  group: ProductGroup;
  collapsed: boolean;
  check: 'none' | 'some' | 'all';
  selectedCount: number;
  onToggleCollapse: () => void;
  onToggleSelection: () => void;
}

/**
 * Een ingeklapte groep moet nog steeds te beslissen zijn: hoeveel staat er, wat
 * is de status ervan, en hoeveel heb je al geselecteerd. Anders is inklappen
 * hetzelfde als verbergen.
 */
function GroupRow({
  group,
  collapsed,
  check,
  selectedCount,
  onToggleCollapse,
  onToggleSelection,
}: GroupRowProps) {
  const summary = collapsed ? groupStatusSummary(group) : [];

  return (
    <div
      className={cn(
        'flex items-center gap-4 border-b bg-[#F7F9FB] px-4',
        collapsed ? 'h-10' : 'h-[34px]',
      )}
    >
      <Checkbox
        checked={check === 'all' ? true : check === 'some' ? 'indeterminate' : false}
        onCheckedChange={onToggleSelection}
        aria-label={`Alles in ${group.label} selecteren`}
      />
      <button
        type="button"
        onClick={onToggleCollapse}
        className="flex flex-1 items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-expanded={!collapsed}
      >
        {collapsed ? (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <span className="text-[13px] font-semibold">{group.label}</span>
        <span className="text-xs text-foreground/50">
          {group.products.length} {group.products.length === 1 ? 'product' : 'producten'}
          {selectedCount > 0 ? ` · ${selectedCount} geselecteerd` : ''}
        </span>
        {summary.length > 0 && (
          <span className="ml-auto flex w-24 shrink-0 items-center justify-end gap-1.5">
            {summary.map(({ status, count }) => (
              <span key={status} className="flex items-center gap-1">
                <span
                  className={cn('h-1.5 w-1.5 rounded-full bg-current', ROW_STATUS[status].tone)}
                />
                <span className="text-xs text-muted-foreground tabular-nums">{count}</span>
              </span>
            ))}
          </span>
        )}
      </button>
      <div className={cn(COL.price, 'text-xs text-muted-foreground')}>
        {groupPriceRange(group)}
      </div>
      <div className={COL.usage} />
      <div className={COL.changed} />
      <div className={COL.reviewers} />
      <div className={COL.active} />
    </div>
  );
}

interface ProductRowProps {
  product: ProductListItem;
  selected: boolean;
  busy: boolean;
  onToggle: (shiftKey: boolean) => void;
  onOpen: () => void;
  onSetEnabled: (enabled: boolean) => void;
}

function ProductRow({ product, selected, busy, onToggle, onOpen, onSetEnabled }: ProductRowProps) {
  const status = ROW_STATUS[rowStatus(product)];
  const muted = Boolean(product.archivedAt) || !product.enabled;

  return (
    <div
      className={cn(
        'flex h-[52px] items-center gap-4 border-b px-4 last:border-b-0',
        selected ? 'bg-[#F4F8FB]' : 'hover:bg-muted/40',
        busy && 'opacity-60',
      )}
    >
      {/* De klik op het vakje mag niet doorlopen naar de rij; shift bewaart de
          reeks-selectie die de gebruiker net begon. */}
      <span
        onClick={(event) => {
          event.stopPropagation();
          onToggle(event.shiftKey);
        }}
        className="flex shrink-0 items-center"
      >
        <Checkbox
          checked={selected}
          aria-label={`${product.name} selecteren`}
          onCheckedChange={() => undefined}
        />
      </span>

      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 flex-col items-start gap-0.5 overflow-hidden text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span
          className={cn(
            'line-clamp-1 w-full text-sm font-semibold',
            muted && 'text-muted-foreground',
          )}
          title={product.name}
        >
          {product.name}
        </span>
        <span className="w-full truncate text-xs text-muted-foreground">{metaLabel(product)}</span>
      </button>

      <div className={cn(COL.status, 'flex items-center gap-2')}>
        <span className={cn('h-[7px] w-[7px] shrink-0 rounded-full bg-current', status.tone)} />
        <span className={cn('text-[13px] font-semibold', status.tone)}>{status.label}</span>
      </div>

      <div className={cn(COL.price, 'text-sm font-semibold tabular-nums', muted && 'text-muted-foreground')}>
        {priceLabel(product)}
      </div>

      <div className={cn(COL.usage, 'text-[13px]', product.usageCount ? '' : 'text-muted-foreground')}>
        {usageLabel(product)}
      </div>

      <div className={cn(COL.changed, 'truncate text-[13px] text-muted-foreground')}>
        {lastChangeLabel(product)}
      </div>

      <div
        className={cn(COL.reviewers, 'items-center gap-1.5')}
        title={
          [
            product.reviewers.map((reviewer) => reviewer.name).join(', '),
            ...(product.reviewGroups ?? []).map(
              (group) => `${group.name}: 1 van ${group.memberCount}`,
            ),
          ]
            .filter(Boolean)
            .join(' · ') || 'Geen reviewers'
        }
      >
        {product.reviewers.length > 0 && (
          <div className="flex -space-x-1.5">
            {product.reviewers.slice(0, 2).map((reviewer) => (
              <span
                key={reviewer.ownerId}
                className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-card bg-[#E0F3FA] text-[10px] font-semibold text-[#006B7C]"
              >
                {reviewer.name.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase()}
              </span>
            ))}
            {product.reviewers.length > 2 && (
              <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-card bg-muted text-[10px] font-semibold text-muted-foreground">
                +{product.reviewers.length - 2}
              </span>
            )}
          </div>
        )}
        {(product.reviewGroups ?? []).slice(0, 2).map((group) => (
          <span
            key={group.id}
            className="truncate rounded-full bg-[#E0F3FA] px-2 py-0.5 text-[10px] font-semibold text-[#006B7C]"
          >
            {group.name}
          </span>
        ))}
        {product.reviewers.length === 0 && (product.reviewGroups ?? []).length === 0 && (
          <span className="text-xs text-muted-foreground">Geen</span>
        )}
      </div>

      <div className={cn(COL.active, 'flex justify-end')}>
        <Switch
          checked={product.enabled}
          disabled={Boolean(product.archivedAt) || busy}
          onCheckedChange={(checked) => onSetEnabled(checked)}
          aria-label={product.enabled ? 'Uitschakelen' : 'Inschakelen'}
        />
      </div>
    </div>
  );
}

export default ProductTable;
